import { alpha, createTheme, Theme } from '@mui/material/styles';
import { StateCreator } from 'zustand';

import { toCssVariables } from './functions/toCssVariables';
import { ThemeState } from './types/ThemeState';
import { ThemeStoreDependencies } from './types/ThemeStoreDependencies';

export const themeStore =
  ({ useLeadChannelStore }: ThemeStoreDependencies): StateCreator<ThemeState> =>
  (_set, get) => ({
    breakpoints: {
      lg1: 1336,
      md1: 872,
      md2: 920,
      sm1: 616,
      sm2: 744,
    },
    colors: {
      alertContrastTextColor: '#ffffff',
      alertErrorBackgroundColor: '#fdeded',
      alertErrorTextColor: '#5f2120',
      alertFilledBackgroundColor: '#323232',
      alertInfoBackgroundColor: '#e5f6fd',
      alertInfoTextColor: '#014361',
      borderColor: '#c0c8d8',
      btnDisabledBorderColor: 'rgba(0, 0, 0, 0.12)',
      btnDisabledColor: '#f5f5f5',
      btnDisabledTextColor: 'rgba(0, 0, 0, 0.26)',
      btnSecondaryColor: '#f5f5f5',
      cardBoxShadowColor: '#11182714',
      checkboxBorderColor: '#cbcbcb',
      checkboxContentColor: '#666666',
      checkboxLabelColor: 'rgba(0, 0, 0, 0.87)',
      drawerBtnColor: '#000000',
      drawerLabelColor: '#999999',
      errorColor: '#d32f2f',
      inputBorderColor: 'rgba(0, 0, 0, 0.23)',
      inputDisabledBorderColor: 'rgba(0, 0, 0, 0.26)',
      inputDisabledColor: '#f5f5f5',
      inputDisabledTextColor: 'rgba(0, 0, 0, 0.38)',
      modalBackdropColor: 'rgba(0, 0, 0, 0.5)',
      modalBackgroundColor: '#ffffff',
      modalDividerColor: '#c0c8d8',
      primaryColor: '#0C4650',
      progressEmptyColor: '#f5f5f5',
      radioBorderColor: '#c0c8d8',
      stepperContentColor: '#666666',
      stepperLineColor: '#c0c8d8',
      successColor: '#5d9e51',
      tooltipArrowColor: 'rgba(97, 97, 97, 0.9)',
      tooltipBackgroundColor: 'rgba(97, 97, 97, 0.92)',
      tooltipTextColor: '#ffffff',
    },
    opacity: {
      selectedTint: 0.16,
    },

    computed: {
      get primaryColor(): string {
        const { leadChannel } = useLeadChannelStore.getState();

        return leadChannel.primaryColor || get().colors.primaryColor;
      },
    },

    createTheme: (): Theme => {
      const {
        computed: { primaryColor },
        colors,
        opacity,
      } = get();

      return createTheme({
        components: {
          /**
           * use MuiCssBaseline for specific theme assignments to custom classes.
           */
          MuiCssBaseline: {
            styleOverrides: {
              '.navigation-pane': {
                '& .MuiButtonBase-root.Mui-selected': {
                  backgroundColor: alpha(primaryColor, opacity.selectedTint),
                  color: primaryColor,
                },
                '& .MuiButtonBase-root.Mui-selected:hover': {
                  backgroundColor: alpha(primaryColor, opacity.selectedTint),
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                  minWidth: '2.5rem',
                },
                '& .MuiListItemText-primary': {
                  color: colors.drawerLabelColor,
                  fontWeight: 500,
                  marginBottom: '1rem',
                  padding: '0 1.5rem',
                },
                '& .MuiListItemText-secondary': {
                  color: 'inherit',
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: '1.5rem',
                },
                a: {
                  borderRadius: '8px',
                  color: colors.drawerLabelColor,
                  height: '4.5rem',
                  marginBottom: '11px',
                  padding: '1.5rem',
                },
              },
            },
          },
          MuiDrawer: {
            defaultProps: {
              PaperProps: {
                style: {
                  padding: '52px 0',
                  width: '17rem',
                },
              },
            },
            styleOverrides: {
              root: {
                '& .navigation-pane a': {
                  borderRadius: '0',
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                fontWeight: 500,
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '&:hover:not(.Mui-error):not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: primaryColor,
                  },
                // MuiOutlinedInput can be an input or select element - query allows us to target both.
                '*[class*="Mui-disabled"]': {
                  backgroundColor: colors.inputDisabledColor,
                },
                borderRadius: '4px',
                fontSize: '1rem',
                fontWeight: 500,
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              root: {
                '& .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                },
              },
            },
          },
          MuiSelect: {
            defaultProps: {
              MenuProps: {
                // Ensures the menu opens below the select field
                anchorOrigin: {
                  horizontal: 'left',
                  vertical: 'bottom',
                },
                // Prevent body scroll locking to avoid address bar repositioning on mobile browsers
                disableScrollLock: true,
                slotProps: {
                  paper: {
                    style: {
                      // On certain smaller devices where the screen height is limited, this will prevent
                      // the menu from taking up the entire screen making the first option of the select inaccessible.
                      maxHeight: 'min(max(240px, 30vw), 400px)',
                      minHeight: '120px',
                      // Prevent scroll chaining inside of the menu (hitting the end of menu wont fling the page)
                      overscrollBehavior: 'contain',
                      // Use instant (non-animated) scrolling to the selected option within menu to prevent scroll jumping issues on mobile devices
                      scrollBehavior: 'auto',
                      // Trigger hardware acceleration to improve mobile scrolling performance (especially older devices)
                      transform: 'translateZ(0)',
                    },
                  },
                },
                // Point of which the popover will attach to the anchor's origin
                transformOrigin: {
                  horizontal: 'left',
                  vertical: 'top',
                },
              },
            },
            styleOverrides: {
              icon: {
                color: 'black',
              },
            },
          },
        },
        palette: {
          primary: {
            main: primaryColor,
          },
        },
        typography: {
          fontFamily: '"Bricolage Grotesque", Verdana, sans-serif',
        },
      });
    },

    publishCssVariables: (): void => {
      const publish = (): void => {
        const {
          colors,
          computed: { primaryColor },
        } = get();

        // defaultPrimaryColor is the brand-neutral default, for surfaces that
        // must not adopt a lead channel's colour. primaryColor last: it is the
        // lead channel override of colors.primaryColor
        Object.entries(
          toCssVariables({
            ...colors,
            defaultPrimaryColor: colors.primaryColor,
            primaryColor,
          }),
        ).forEach(([name, value]): void => {
          document.documentElement.style.setProperty(name, String(value));
        });
      };

      publish();

      // The lead channel resolves after bootstrap, so republish once its
      // branding arrives
      useLeadChannelStore.subscribe(publish);
    },
  });
